// src/lib/sse.js
import { CONFIG } from '../config';
import { getAuthClient } from '../auth/authClient';
import { AUTH_ENABLED } from '../auth/flags';

/**
 * Consume a POST Server-Sent-Events stream as an async iterable of
 * { event, data } (data is JSON-parsed). Generic — event names are not
 * interpreted here. See docs/sse-quiz-grading-api-contract.md.
 *
 * Throws before yielding on a non-2xx response, carrying .code/.status/.data
 * (same shape as apiFetch). Throws { code: 'STREAM_STALLED' } if no bytes
 * arrive within stallMs. Propagates AbortError when opts.signal aborts.
 */
export async function* postEventStream(path, body, { signal, stallMs = 20000 } = {}) {
  const base = (CONFIG && CONFIG.API_BASE_URL) ? CONFIG.API_BASE_URL : window.location.origin;
  const url = new URL(path, base);

  // FormData/Blob bodies go over the wire untouched — the browser sets the
  // multipart boundary in Content-Type, so we must NOT set it ourselves (same
  // rule apiFetch follows). Plain objects are JSON-encoded as before.
  const isRawBody = (typeof FormData !== 'undefined' && body instanceof FormData)
    || (typeof Blob !== 'undefined' && body instanceof Blob);

  const headers = { 'Accept': 'text/event-stream' };
  if (!isRawBody) headers['Content-Type'] = 'application/json';
  if (AUTH_ENABLED) {
    try {
      const idToken = await getAuthClient().getIdToken();
      if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    } catch { /* proceed; the server will 401 and we throw below */ }
  }

  const response = await fetch(url.toString(), {
    method: 'POST', headers, credentials: 'include', body: isRawBody ? body : JSON.stringify(body), signal,
  });

  if (!response.ok) {
    let data, code, message = `HTTP ${response.status}`;
    try { data = await response.json(); code = data?.error ?? data?.code; message = data?.message ?? code ?? message; } catch { /* non-JSON body */ }
    const err = new Error(message);
    err.code = code; err.status = response.status; err.data = data;
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '', event = '', dataStr = '';

  try {
    while (true) {
      let stallTimer;
      const stall = new Promise((_, reject) => {
        stallTimer = setTimeout(() => { const e = new Error('Stream stalled'); e.code = 'STREAM_STALLED'; reject(e); }, stallMs);
      });
      let result;
      try { result = await Promise.race([reader.read(), stall]); }
      finally { clearTimeout(stallTimer); }

      if (result.done) break;
      buffer += decoder.decode(result.value, { stream: true });
      const rawLines = buffer.split('\n');
      buffer = rawLines.pop() ?? '';
      for (const rawLine of rawLines) {
        // SSE terminators may be LF or CRLF (the spec allows both). Strip a
        // trailing \r so the blank-line frame boundary is a literal '' either
        // way — otherwise CRLF streams drop every non-final frame.
        const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
        if (line.startsWith(':')) continue;           // comment / heartbeat
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataStr = line.slice(5).trim();
        else if (line === '' && event && dataStr) { yield { event, data: JSON.parse(dataStr) }; event = ''; dataStr = ''; }
      }
    }
    if (buffer.startsWith('data:')) dataStr = buffer.slice(5).trim();
    else if (buffer.startsWith('event:')) event = buffer.slice(6).trim();
    if (event && dataStr) yield { event, data: JSON.parse(dataStr) };
  } catch (e) {
    if (e && e.code === 'STREAM_STALLED') { try { await reader.cancel(); } catch { /* already closed */ } }
    throw e;
  } finally {
    try { reader.releaseLock(); } catch { /* noop */ }
  }
}
